import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Mail, Lock, Eye, EyeOff, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import MobileBottomNav from '../components/MobileBottomNav';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const { theme } = useTheme();
  const currentLogo = theme === 'light' 
    ? "https://customer-assets.emergentagent.com/job_9d222022-9462-4aac-aca8-59da15d4f381/artifacts/tueqptuk_registan%20logo.png"
    : "https://customer-assets.emergentagent.com/job_422444ca-8f0f-4ebc-8f78-bee66ce7f723/artifacts/qo6wuzuf_REGISTAN%20LOGO.png";
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, formData);
      
      // Use AuthContext to save login state
      login(response.data.user, response.data.token);
      
      // Redirect to dashboard
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <Helmet>
        <title>Login - Registan Tourism</title>
        <link rel="canonical" href="https://registantours.com/login" />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <button className="auth-close-btn" onClick={() => navigate('/')}>
        <X size={24} />
      </button>
      <div className="auth-container">
        <div className="auth-header">
          <img src={currentLogo} alt="Registan" className="auth-logo-img" />
          <h1>{t('welcomeBack')}</h1>
          <p>{t('signInAccess')}</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="auth-error">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">{t('emailAddress')}</label>
            <div className="input-with-icon">
              <Mail size={20} />
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your.email@example.com"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('password')}</label>
            <div className="input-with-icon">
              <Lock size={20} />
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={t('enterPassword')}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div className="auth-options">
            <Link to="/forgot-password" className="forgot-password">
              {t('forgotPassword')}
            </Link>
          </div>

          <button 
            type="submit" 
            className="btn-primary btn-auth"
            disabled={loading}
          >
            {loading ? t('signingIn') : t('signIn')}
          </button>
        </form>

        <div className="auth-footer">
          <p>{t('dontHaveAccount')} <Link to="/register">{t('createAccount')}</Link></p>
          <p style={{ marginTop: '12px', fontSize: '12px', opacity: 0.7 }}>
            <Link to="/privacy" style={{ color: 'inherit' }} data-testid="auth-privacy-link">{t('privacyPolicy') || 'Privacy Policy'}</Link>
          </p>
        </div>
      </div>
      
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
};

export default LoginPage;
